import axios from 'axios';

// Create axios instance with base configuration
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000/api',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Create public axios instance (no auth token)
export const publicApi = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000/api',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Auth token resolver: an impersonated partner tab keeps its own token in
// sessionStorage (per-tab), so it never clashes with the admin login in
// shared localStorage. Everywhere must use this instead of reading storage.
export const getAuthToken = () => {
  try {
    return sessionStorage.getItem('impersonate_token') || localStorage.getItem('token');
  } catch {
    return localStorage.getItem('token');
  }
};

// Request interceptor to add auth token (only for authenticated routes)
api.interceptors.request.use(
  (config) => {
    let token = null;
    try {
      // Impersonated partner tab: its own login, never the shared one.
      token = sessionStorage.getItem('impersonate_token') || null;
      // Admin tab holding a stale partner login: heal from the admin backup
      // so every /admin request goes out as admin. Partner pages (which carry
      // their own session key above) are never touched by this.
      if (!token && window.location.pathname.startsWith('/admin')) {
        const raw = localStorage.getItem('by_admin_backup');
        const cur = JSON.parse(localStorage.getItem('user') || 'null');
        if (raw && (!cur || cur.role !== 'admin')) {
          const { token: backupToken, user: backupUser } = JSON.parse(raw);
          if (backupToken && backupUser?.role === 'admin') {
            localStorage.setItem('token', backupToken);
            localStorage.setItem('user', JSON.stringify(backupUser));
            token = backupToken;
          }
        }
      }
    } catch {
      /* fall through to shared login */
    }
    if (!token) token = getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      try {
        // Dead impersonation session: drop tab-local keys (admin backup stays for Exit).
        if (sessionStorage.getItem('impersonate_token')) {
          sessionStorage.removeItem('impersonate_token');
          sessionStorage.removeItem('impersonate_user');
        }
      } catch {
        /* ignore */
      }
      const url = error.config?.url || '';
      // Login/OTP attempts return 401 on bad credentials by design —
      // show the error inline instead of redirecting away.
      const isLoginAttempt = /\/login$/.test(url) || url.includes('/verify-otp') || url.includes('/send-otp');
      if (!isLoginAttempt) {
        // Token expired or invalid
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = window.location.pathname.startsWith('/admin') ? '/admin/login' : '/login';
      }
    }
    return Promise.reject(error);
  }
);

// API endpoints
export const authAPI = {
  // User authentication
  login: (credentials) => api.post('/auth/login', credentials),
  register: (userData) => api.post('/auth/register', userData),
  registerPartner: (userData) => api.post('/auth/register-partner', userData),
  registerBusPartner: (userData) => api.post('/auth/register-bus-partner', userData),
  registerRestaurantPartner: (userData) => api.post('/auth/register-restaurant-partner', userData),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, password) => api.put(`/auth/reset-password/${token}`, { password }),
  verifyEmail: (token) => publicApi.get(`/auth/verify-email/${token}`),
  resendVerification: (email) => api.post('/auth/resend-verification', { email }),
  changePassword: (data) => api.put('/auth/change-password', data),
  
  // Mobile OTP login
  sendOtp: (phone) => api.post('/auth/send-otp', { phone }),
  verifyOtp: (phone, otp) => api.post('/auth/verify-otp', { phone, otp }),
  getSocialConfig: () => publicApi.get('/auth/social-config'),
  socialLogin: (provider, token, extra = {}) => api.post('/auth/social', { provider, token, ...extra }),
  
  // Admin authentication (OTP-secured: credentials -> email OTP -> tokens)
  adminLogin: (credentials) => api.post('/admin/login', credentials),
  adminVerifyOtp: (data) => api.post('/admin/verify-otp', data),
  
  // Get current user
  getCurrentUser: () => api.get('/auth/me'),
};

export const userAPI = {
  getProfile: () => api.get('/users/profile'),
  updateProfile: (data) => api.put('/users/profile', data),
  requestEmailChange: (email) => api.post('/users/profile/email', { email }),
  uploadProfileImage: (formData) => api.post('/users/profile/image', formData),
  getBookings: () => api.get('/users/bookings'),
  getMyTrips: () => api.get('/users/trips'),
  getTripInvoice: (type, id) => api.get(`/users/trips/${type}/${id}/invoice`, { responseType: 'text' }),
  cancelTrip: (type, id, reason) => api.put(`/users/trips/${type}/${id}/cancel`, { reason }),
  getApiHotelCancelPreview: (id) => api.get(`/users/trips/api-hotel/${id}/cancel-preview`),
  // Flight quote can take ~30s while the supplier processes it
  getFlightCancelPreview: (id) => api.get(`/users/trips/flight/${id}/cancel-preview`, { timeout: 60000 }),
  getCoTravellers: () => api.get('/users/co-travellers'),
  addCoTraveller: (data) => api.post('/users/co-travellers', data),
  updateCoTraveller: (id, data) => api.put(`/users/co-travellers/${id}`, data),
  deleteCoTraveller: (id) => api.delete(`/users/co-travellers/${id}`),
};

export const walletAPI = {
  getOverview: () => api.get('/wallet/overview'),
  getTravelTransactions: (page = 1, limit = 10) => api.get('/wallet/travel-transactions', { params: { page, limit } }),
  createTopUpOrder: (data) => api.post('/wallet/top-up-razorpay', data),
  confirmTopUp: (data) => api.post('/wallet/top-up-confirm', data),
  payRestaurantBill: (data) => api.post('/wallet/pay-restaurant', data),
  payHotelBooking: (data) => api.post('/wallet/pay-hotel', data),
  payFlightBooking: (data) => api.post('/wallet/pay-flight', data),
  refundFlight: (data) => api.post('/wallet/refund-flight', data),
};

export const hotelAPI = {
  // Public hotel endpoints (use publicApi to avoid auth token issues)
  searchHotels: (params) => publicApi.get('/hotels/search', { params }),
  getHotel: (id) => publicApi.get(`/hotels/${id}`),
  getHotelWithDates: (id, checkIn, checkOut) =>
    publicApi.get(`/hotels/${id}`, { params: { checkIn, checkOut } }),
  getHotelBySlug: (slug) => publicApi.get(`/hotels/slug/${slug}`),
  getSimilarHotels: (hotelId) => publicApi.get(`/hotels/${hotelId}/similar`),
  getHotelRooms: (hotelId, params) => publicApi.get(`/hotels/${hotelId}/rooms`, { params }),
  getRoomAvailability: (hotelId, roomId, params) => 
    publicApi.get(`/hotels/${hotelId}/rooms/${roomId}/availability`, { params }),
  getPopularDestinations: () => publicApi.get('/hotels/destinations/popular'),
  
  // Public attributes endpoint
  getActiveAttributes: () => publicApi.get('/admin/attributes/active'),
  
  // Trawex live hotel search (public)
  searchTrawexHotels: (data) => publicApi.post('/hotel-api/search', data),
  moreTrawexHotels: (data) => publicApi.post('/hotel-api/more-results', data),
  moreTrawexHotelsPagination: (data) => publicApi.post('/hotel-api/more-results-pagination', data),
  // Trawex live hotel details: room rates for one hotel (sessionId + tokenId + productId + hotelId)
  getTrawexHotelDetails: (data) => publicApi.post('/hotel-api/revalidate', data),
  // Trawex full hotel content: photos, description, reviews, contact, coordinates
  getTrawexHotelContent: (data) => publicApi.post('/hotel-api/hotel-details', data),
  // Trawex revalidate one room rate before booking (sessionId + productId + tokenId + rateBasisId)
  validateTrawexRate: (data) => publicApi.post('/hotel-api/validate-rate', data),
  // Trawex hotel API markup rules (platform fee etc., shown separately on booking page)
  getHotelApiMarkup: (params) => publicApi.get('/hotel-api-markup/active', { params }),
  // Trawex hotel API topup commission (merged into Available Rooms prices on details page)
  getHotelApiTopup: (params) => publicApi.get('/hotel-api-topup/active', { params }),
  // Trawex confirmed booking (must run AFTER validate-rate)
  bookTrawexHotel: (data) => publicApi.post('/hotel-api/book', data),
  // Trawex Booking Details Console (post-booking lookup by supplierConfirmationNum/referenceNum)
  getTrawexBookingDetails: (data) => publicApi.post('/hotel-api/booking-details', data),
  // Trawex Cancel Booking Console (full/partial cancel by supplierConfirmationNum/referenceNum)
  cancelTrawexBooking: (data) => publicApi.post('/hotel-api/cancel-booking', data),
  // Trawex Static Content Console (bulk static inventory ?from&to&city&country)
  getTrawexStaticContent: (params) => publicApi.get('/hotel-api/static-content', { params }),
  filterTrawexHotels: (data) => publicApi.post('/hotel-api/filter-results', data),
  moreFilterTrawexHotels: (data) => publicApi.post('/hotel-api/more-filter-results', data),
  filterTrawexHotelsPagination: (data) => publicApi.post('/hotel-api/filter-results-pagination', data),

  // Amadeus hotel search
  searchAmadeusHotels: (params) => publicApi.get('/hotels/amadeus-search', { params }),
  
  // Admin hotel endpoints
  getAllHotels: (params) => api.get('/admin/hotels', { params }),
  getAdminHotel: (id) => api.get(`/admin/hotels/${id}`),
  createHotel: (data) => api.post('/admin/hotels', data),
  updateHotel: (id, data) => api.put(`/admin/hotels/${id}`, data),
  deleteHotel: (id) => api.delete(`/admin/hotels/${id}`),
  permanentDeleteHotel: (id) => api.delete(`/admin/hotels/${id}/permanent`),
  getDeletedHotels: (params) => api.get('/admin/hotels/deleted', { params }),
  getHotelStats: () => api.get('/admin/hotels/stats'),
  restoreHotel: (id, data) => api.put(`/admin/hotels/${id}/restore`, data || {}),
  toggleHotelStatus: (id) => api.patch(`/admin/hotels/${id}/toggle-status`),
  toggleHotelFeatured: (id) => api.patch(`/admin/hotels/${id}/toggle-featured`),
  getHotelPartner: (id) => api.get(`/admin/hotels/${id}/partner`),
  updateHotelPartner: (id, data) => api.put(`/admin/hotels/${id}/partner`, data),
  impersonate: (userId) => api.post('/admin/impersonate', { userId }),
  
  // Room management
  getAdminHotelRooms: (hotelId) => api.get(`/admin/hotels/${hotelId}/rooms`),
  createRoom: (hotelId, data) => api.post(`/admin/hotels/${hotelId}/rooms`, data),
  updateRoom: (hotelId, roomId, data) => api.put(`/admin/hotels/${hotelId}/rooms/${roomId}`, data),
  deleteRoom: (hotelId, roomId) => api.delete(`/admin/hotels/${hotelId}/rooms/${roomId}`),

  // Date-wise rates (same endpoints as partner panel — admin is authorized server-side)
  getPartnerRates: (hotelId, params) => api.get(`/partner/hotel/hotels/${hotelId}/rates`, { params }),
  savePartnerRates: (hotelId, data) => api.put(`/partner/hotel/hotels/${hotelId}/rates`, data),
  deletePartnerRates: (hotelId, data) => api.delete(`/partner/hotel/hotels/${hotelId}/rates`, { data }),
};

export const reviewAPI = {
  getReviews: (hotelId) => publicApi.get(`/reviews/${hotelId}`),
  createReview: (hotelId, data) => api.post(`/reviews/${hotelId}`, data),
  getRestaurantReviews: (restaurantId, params) => publicApi.get(`/restaurant-reviews/${restaurantId}`, { params }),
  createRestaurantReview: (restaurantId, data) => api.post(`/restaurant-reviews/${restaurantId}`, data),
  getPartnerReviews: (params) => api.get('/partner/restaurant-reviews', { params }),
  replyToRestaurantReview: (id, data) => api.put(`/partner/restaurant-reviews/${id}/reply`, data),
};

export const uploadAPI = {
  hotelImage: (hotelId, formData) => api.post(`/upload/hotel/${hotelId}`, formData),
  hotelImages: (hotelId, formData) => api.post(`/upload/hotel/${hotelId}?multiple=true`, formData),
  roomImage: (hotelId, roomId, formData) => api.post(`/upload/hotel/${hotelId}/room/${roomId}`, formData),
  roomImages: (hotelId, roomId, formData) => api.post(`/upload/hotel/${hotelId}/room/${roomId}?multiple=true`, formData),
  reviewPhoto: (hotelId, formData) => api.post(`/upload/review/${hotelId}`, formData),
  restaurantImage: (restaurantId, formData) => api.post(`/upload/restaurant/${restaurantId}`, formData),
  restaurantImages: (restaurantId, formData) => api.post(`/upload/restaurant/${restaurantId}?multiple=true`, formData),
  deleteRestaurantImage: (restaurantId, url) => api.delete(`/upload/restaurant/${restaurantId}/image?url=${encodeURIComponent(url)}`),
  restaurantMenuImage: (restaurantId, formData) => api.post(`/upload/restaurant/${restaurantId}/menu`, formData),
  deleteRestaurantMenuImage: (restaurantId, url) => api.delete(`/upload/restaurant/${restaurantId}/menu`, { params: { url } }),
  genericFile: (formData) => api.post('/upload', formData),
};

export const partnerAPI = {
  // Partner hotel management
  getPartnerHotels: () => api.get('/partner/hotel/hotels'),
  getPartnerDeletedHotels: (params) => api.get('/partner/hotel/hotels/deleted', { params }),
  recoverPartnerHotel: (id) => api.post(`/partner/hotel/hotels/${id}/recover`),
  regenerateChannelKey: (id) => api.post(`/partner/hotel/hotels/${id}/regenerate-channel-key`),
  // Channel manager integration (master, any provider)
  getChannelManagers: (hotelId) => api.get(`/partner/hotel/hotels/${hotelId}/channel-managers`),
  saveChannelManager: (hotelId, provider, config) => api.put(`/partner/hotel/hotels/${hotelId}/channel-managers/${provider}`, config),
  testChannelManager: (hotelId, provider) => api.post(`/partner/hotel/hotels/${hotelId}/channel-managers/${provider}/test`),
  syncChannelManagerNow: (hotelId, provider) => api.post(`/partner/hotel/hotels/${hotelId}/channel-managers/${provider}/sync`),
  disconnectChannelManager: (hotelId, provider) => api.delete(`/partner/hotel/hotels/${hotelId}/channel-managers/${provider}`),
  getPartnerHotel: (id) => api.get(`/partner/hotel/hotels/${id}`),
  createPartnerHotel: (data) => api.post('/partner/hotel/hotels', data),
  updatePartnerHotel: (id, data) => api.put(`/partner/hotel/hotels/${id}`, data),
  togglePartnerHotelActive: (id) => api.put(`/partner/hotel/hotels/${id}/toggle-active`),
  
  // Partner room management
  getPartnerRooms: (hotelId) => api.get(`/partner/hotel/hotels/${hotelId}/rooms`),
  getPartnerRoom: (hotelId, roomId) => api.get(`/partner/hotel/hotels/${hotelId}/rooms/${roomId}`),
  createPartnerRoom: (hotelId, data) => api.post(`/partner/hotel/hotels/${hotelId}/rooms`, data),
  updatePartnerRoom: (hotelId, roomId, data) => api.put(`/partner/hotel/hotels/${hotelId}/rooms/${roomId}`, data),
  deletePartnerRoom: (hotelId, roomId) => api.delete(`/partner/hotel/hotels/${hotelId}/rooms/${roomId}`),
  
  // Partner date-wise rate management (one room + one date = one rate)
  getPartnerRates: (hotelId, params) => api.get(`/partner/hotel/hotels/${hotelId}/rates`, { params }),
  savePartnerRates: (hotelId, data) => api.put(`/partner/hotel/hotels/${hotelId}/rates`, data),
  deletePartnerRates: (hotelId, data) => 
    api.delete(`/partner/hotel/hotels/${hotelId}/rates`, { data }),

  // Unified per-package ARI (one structure: packages = rate IDs)
  saveUnifiedAri: (hotelId, data) => api.put(`/inventory/manage/hotels/${hotelId}/ari`, data),
  getUnifiedAri: (hotelId, params) => api.get(`/inventory/manage/hotels/${hotelId}/ari`, { params }),
  getUnifiedPackages: (hotelId) => api.get(`/inventory/manage/hotels/${hotelId}/packages`),
  getUnifiedMappings: (hotelId) => api.get(`/inventory/manage/hotels/${hotelId}/mappings`),
  // Partner bookings
  getPartnerBookings: (params) => api.get('/partner/hotel/bookings', { params }),
  getPartnerBookingStats: (params) => api.get('/partner/hotel/bookings/stats', { params }),
  cancelPartnerBooking: (id, data) => api.put(`/partner/hotel/bookings/${id}/cancel`, data),
  exportPartnerBookings: (params) => api.get('/partner/hotel/bookings/export', { params, responseType: 'blob' }),
  getPartnerBookingInvoice: (id) => api.get(`/partner/hotel/bookings/${id}/invoice`, { responseType: 'blob' }),
  
  // Partner dashboard
  getPartnerDashboard: () => api.get('/partner/hotel/dashboard'),

  // Partner analytics
  getPartnerAnalytics: (params) => api.get('/partner/hotel/analytics', { params }),
  getPartnerHotelAttributes: (hotelId) => api.get('/partner/hotel/attributes', { params: hotelId ? { hotelId } : {} }),
  getPartnerRoomAttributes: (hotelId, roomId) => api.get('/partner/hotel/room-attributes', { params: { hotelId, roomId } }),
  createPartnerRoomAttribute: (data) => api.post('/partner/hotel/room-attributes', data),
  togglePartnerRoomAttribute: (id, hotelId, roomId) => api.patch(`/partner/hotel/room-attributes/${id}/toggle`, {}, { params: { hotelId, roomId } }),
  updatePartnerRoomAttribute: (id, data) => api.put(`/partner/hotel/room-attributes/${id}`, data),
  deletePartnerRoomAttribute: (id) => api.delete(`/partner/hotel/room-attributes/${id}`),
  createPartnerHotelAttribute: (data) => api.post('/partner/hotel/attributes', data),
  updatePartnerHotelAttribute: (id, data) => api.put(`/partner/hotel/attributes/${id}`, data),
  togglePartnerHotelAttribute: (id, hotelId) => api.patch(`/partner/hotel/attributes/${id}/toggle`, {}, { params: { hotelId } }),
  deletePartnerHotelAttribute: (id) => api.delete(`/partner/hotel/attributes/${id}`),

  // Partner profile
  getPartnerProfile: () => api.get('/partner/profile'),
  updatePartnerProfile: (data) => api.put('/partner/profile', data),
  changePartnerEmail: (data) => api.put('/partner/profile/email', data),
  changePartnerPassword: (data) => api.put('/auth/change-password', data),
  uploadPartnerDocument: (formData) => api.post('/partner/profile/documents', formData),
  uploadPartnerAvatar: (formData) => api.post('/partner/profile/avatar', formData),
};

export const flightCouponAPI = {
  validate: (data) => publicApi.post('/flight-coupon/validate', data),
  getAll: () => api.get('/admin/flight-coupon'),
  create: (data) => api.post('/admin/flight-coupon', data),
  update: (id, data) => api.put(`/admin/flight-coupon/${id}`, data),
  remove: (id) => api.delete(`/admin/flight-coupon/${id}`),
};

export const flightAPI = {
  searchFlights: (params) => api.get('/flights/search', { params }),
  searchFlightsPublic: (params) => publicApi.get('/flights/search', { params, timeout: 120000 }),
  searchAirports: (q) => publicApi.get('/flights/airports', { params: q ? { search: q } : {} }),
  getFlight: (id) => api.get(`/flights/${id}`),
  getStats: () => api.get('/flights/stats/overview'),
  // Trawex live steps (revalidate / fare-rules / extra-services are public; book needs login)
  trawexRevalidate: (data) => publicApi.post('/flights/trawex/revalidate', data),
  trawexFareRules: (data) => publicApi.post('/flights/trawex/fare-rules', data),
  trawexExtraServices: (data) => publicApi.post('/flights/trawex/extra-services', data),
  trawexBook: (data) => api.post('/flights/trawex/book', data),
  trawexTicketOrder: (data) => api.post('/flights/trawex/ticket-order', data),
  trawexCompleteTrip: (data) => api.post('/flights/trawex/complete-trip', data),
  trawexTripDetails: (data) => api.post('/flights/trawex/trip-details', data),
  trawexCancel: (data) => api.post('/flights/trawex/cancel', data),
  trawexBookingNotes: (data) => api.post('/flights/trawex/booking-notes', data),
  trawexAirportsLive: () => publicApi.get('/flights/trawex/airports-live'),
  trawexAirlinesLive: () => publicApi.get('/flights/trawex/airlines-live'),
  trawexPtrStatus: (data) => api.post('/flights/trawex/ptr-status', data),
  trawexVoidQuote: (data) => api.post('/flights/trawex/void-quote', data),
  trawexVoid: (data) => api.post('/flights/trawex/void', data),
  trawexReissueQuote: (data) => api.post('/flights/trawex/reissue-quote', data),
  trawexReissue: (data) => api.post('/flights/trawex/reissue', data),
  trawexRefundQuote: (data) => api.post('/flights/trawex/refund-quote', data),
  trawexRefund: (data) => api.post('/flights/trawex/refund', data),

  // Admin flight endpoints
  getAllFlights: (params) => api.get('/flights', { params }),
  createFlight: (data) => api.post('/flights', data),
  updateFlight: (id, data) => api.put(`/flights/${id}`, data),
  deleteFlight: (id) => api.delete(`/flights/${id}`),
};

export const bookingAPI = {
  // Create bookings
  createBooking: (data) => api.post('/bookings', data),
  createHotelBooking: (data) => api.post('/bookings/hotel', data),
  createFlightBooking: (data) => api.post('/bookings/flight', data),
  
  // Get bookings
  getBooking: (id) => api.get(`/bookings/${id}`),
  getUserBookings: () => api.get('/bookings'),
  
  // Booking actions
  cancelBooking: (id) => api.put(`/bookings/${id}/cancel`),
  confirmBooking: (id, paymentData = {}) => api.put(`/bookings/${id}/confirm`, { 
    paymentStatus: 'paid',
    ...paymentData
  }),
  
  resendTicket: (id, email) => api.post(`/bookings/${id}/resend-ticket`, email ? { email } : {}),

  // Admin booking endpoints
  getAllBookings: (params) => api.get('/bookings/admin/all', { params }),
  updateBookingStatus: (id, status) => api.put(`/admin/bookings/${id}/status`, { status }),
};

export const couponAPI = {
  getAll: (params) => api.get('/admin/hotel-coupons', { params }),
  getById: (id) => api.get(`/admin/hotel-coupons/${id}`),
  create: (data) => api.post('/admin/hotel-coupons', data),
  update: (id, data) => api.put(`/admin/hotel-coupons/${id}`, data),
  delete: (id) => api.delete(`/admin/hotel-coupons/${id}`),
  toggleStatus: (id) => api.patch(`/admin/hotel-coupons/${id}/toggle-status`),
  getActive: (params) => publicApi.get('/hotel-coupons/active', { params }),
  validate: (code, amount, hotelSource, hotelId) => publicApi.post('/hotel-coupons/validate', { code, amount, hotelSource, hotelId }),
};

export const hotelMarkupAPI = {
  getAll: () => api.get('/admin/hotel-markup'),
  getById: (id) => api.get(`/admin/hotel-markup/${id}`),
  create: (data) => api.post('/admin/hotel-markup', data),
  update: (id, data) => api.put(`/admin/hotel-markup/${id}`, data),
  delete: (id) => api.delete(`/admin/hotel-markup/${id}`),
  getByHotel: (hotelId) => publicApi.get(`/hotel-markup/hotel/${hotelId}`),
};

export const taxAPI = {
  // Public tax calculation
  calculateTax: (amount, country = 'IN', hotelId = null) =>
    publicApi.post('/admin/taxes/calculate', { amount, country, hotelId }),

  // Full configuration (TaxManagement UI)
  getConfiguration: () => api.get('/admin/taxes/config'),
  saveConfiguration: (data) => api.put('/admin/taxes/config', data),

  // Admin CRUD for individual tax entries
  getAllTaxes: () => api.get('/admin/taxes'),
  getTaxById: (id) => api.get(`/admin/taxes/${id}`),
  createTax: (data) => api.post('/admin/taxes', data),
  updateTax: (id, data) => api.put(`/admin/taxes/${id}`, data),
  deleteTax: (id) => api.delete(`/admin/taxes/${id}`),
};

export const adminAPI = {
  hotelPayoutRequests: (params) => api.get('/partner/hotel-payout/admin/requests', { params }),
  hotelPayoutAction: (id, action, data) => api[action === 'status' ? 'get' : 'put'](`/partner/hotel-payout/admin/${id}/${action}`, data),
  hotelPartnerWalletBalance: (params) => api.get('/partner/hotel-payout/admin/wallet-balance', { params }),
  getRoomAttributes: () => api.get('/admin/room-attributes'),
  createRoomAttribute: (data) => api.post('/admin/room-attributes', data),
  updateRoomAttribute: (id, data) => api.put(`/admin/room-attributes/${id}`, data),
  toggleRoomAttribute: (id) => api.patch(`/admin/room-attributes/${id}/toggle`, {}),
  deleteRoomAttribute: (id) => api.delete(`/admin/room-attributes/${id}`),
  hotelPayoutManualPay: (id, formData) => api.put(`/partner/hotel-payout/admin/${id}/pay-manual`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  // Dashboard stats
  getDashboardStats: () => api.get('/admin/dashboard'),
  
  // User management
  getAllUsers: () => api.get('/admin/users'),
  updateUserStatus: (id, status) => api.put(`/admin/users/${id}/status`, { status }),
  
  // System settings
  getSettings: () => api.get('/admin/settings'),
  updateSettings: (data) => api.put('/admin/settings', data),
  getBackgrounds: () => api.get('/settings/backgrounds'),
  uploadBackground: (formData) => api.post('/settings/backgrounds', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  setActiveBackground: (filename) => api.post('/settings/backgrounds/active', { filename }),
  deleteBackground: (filename) => api.delete('/settings/backgrounds', { data: { filename } }),
  getActiveBackground: () => publicApi.get('/settings/backgrounds/active'),

  // Business modules
  getBusinesses: () => publicApi.get('/settings/businesses'),
  getAllBusinesses: () => api.get('/settings/businesses/all'),
  toggleBusiness: (id, enabled) => api.post('/settings/businesses/toggle', { id, enabled }),
  getBusinessIcons: (id, type = 'desktop') => api.get(`/settings/businesses/icons/${id}/${type}`),
  uploadBusinessIcon: (id, formData, type = 'desktop') => api.post(`/settings/businesses/icon/${id}/${type}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  selectBusinessIcon: (id, filename, type = 'desktop') => api.post(`/settings/businesses/icon/${id}/${type}/select`, { filename }),
  deleteBusinessIcon: (id, filename, type = 'desktop') => api.delete(`/settings/businesses/icon/${id}/${type}`, { data: { filename } }),

  // Home page offers
  getOffersAll: () => api.get('/settings/offers/all'),
  createOffer: (formData) => api.post('/settings/offers', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  updateOffer: (id, formData) => api.put(`/settings/offers/${id}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deleteOffer: (id) => api.delete(`/settings/offers/${id}`),
  getOfferImages: () => api.get('/settings/offers/images'),
  uploadOfferImage: (formData) => api.post('/settings/offers/images/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deleteOfferImage: (filename) => api.delete('/settings/offers/images', { data: { filename } }),
};

export const pageAPI = {
  // Public endpoints
  getPages: () => api.get('/pages'),
  getPageBySlug: (slug) => api.get(`/pages/slug/${slug}`),

  // Admin endpoints
  getAdminPages: () => api.get('/pages/admin'),
  getAdminPage: (id) => api.get(`/pages/admin/${id}`),
  createPage: (data) => api.post('/pages/admin', data),
  updatePage: (id, data) => api.put(`/pages/admin/${id}`, data),
  deletePage: (id) => api.delete(`/pages/admin/${id}`),
};


export const agentAPI = {
  register: (data) => api.post('/agent/register', data),
  getProfile: () => api.get('/agent/profile'),
  updateProfile: (data) => api.put('/agent/profile', data),
  getDashboard: () => api.get('/agent/dashboard'),
  getBookings: (params) => api.get('/agent/bookings', { params }),
};

export const corporateAPI = {
  register: (data) => api.post('/corporate/register', data),
  getProfile: () => api.get('/corporate/profile'),
  updateProfile: (data) => api.put('/corporate/profile', data),
  getDashboard: () => api.get('/corporate/dashboard'),
  getEmployees: () => api.get('/corporate/employees'),
  addEmployee: (data) => api.post('/corporate/employees', data),
  removeEmployee: (userId) => api.delete(`/corporate/employees/${userId}`),
};

export const restaurantAPI = {
  searchRestaurants: (params) => api.get('/restaurants/search', { params }),
  getPopularRestaurants: (params) => api.get('/restaurants/popular', { params }),
  getOffers: (params) => api.get('/restaurants/offers', { params }),
  getRelatedRestaurants: (restaurantId, params) => api.get(`/restaurants/related/${restaurantId}`, { params }),
  getPublicCuisines: () => api.get('/restaurants/cuisines'),
  getRestaurant: (id) => api.get(`/restaurants/${id}`),
  getMenu: (id) => api.get(`/restaurants/${id}/menu`),
  createBooking: (data) => api.post('/restaurants/book', data),
  confirmBooking: (id, paymentData) => api.put(`/restaurants/booking/${id}/confirm`, paymentData),
  getBooking: (reference) => api.get(`/restaurants/booking/${reference}`),
  cancelBooking: (id, reason) => api.put(`/restaurants/booking/${id}/cancel`, { reason }),
  getMyBookings: () => api.get('/restaurants/bookings/my'),
  assignTablesToBooking: (id, tableIds) => api.put(`/restaurants/booking/${id}/assign-tables`, { tableIds }),
  completeBooking: (id) => api.put(`/restaurants/booking/${id}/complete`),

  // Payment flows
  confirmBookingPayment: (id, data) => api.put(`/restaurants/booking/${id}/confirm-payment`, data),
  reportPaymentFailed: (id, data) => api.post(`/restaurants/booking/${id}/payment-failed`, data),
  reportWalkinPaymentFailed: (data) => api.post('/restaurants/walkin/payment-failed', data),
  settleBooking: (id, data) => api.put(`/restaurants/booking/${id}/settle`, data),
  payRemainingBooking: (id, data) => api.put(`/restaurants/booking/${id}/pay-remaining`, data),

  // Packages
  getPackages: (restaurantId) => api.get(`/restaurant-packages/${restaurantId}`),
  getRestaurantPackages: (params) => api.get('/partner/restaurant-packages', { params }),
  createPackage: (data) => api.post('/partner/restaurant-packages', data),
  updatePackage: (id, data) => api.put(`/partner/restaurant-packages/${id}`, data),
  deletePackage: (id) => api.delete(`/partner/restaurant-packages/${id}`),

  // Booking advance settings
  resolveBookingAdvance: (restaurantId) => api.get(`/restaurant-booking-advance/resolve/${restaurantId}`),
  getBookingAdvanceSettings: () => api.get('/restaurant-booking-advance'),
  createBookingAdvanceSetting: (data) => api.post('/restaurant-booking-advance', data),
  updateBookingAdvanceSetting: (id, data) => api.put(`/restaurant-booking-advance/${id}`, data),
  deleteBookingAdvanceSetting: (id) => api.delete(`/restaurant-booking-advance/${id}`),

  // Restaurant cancellation settings
  resolveCancellationCharge: (restaurantId, params) => api.get(`/restaurant-cancellation-settings/resolve/${restaurantId}`, { params }),
  getCancellationSettings: () => api.get('/restaurant-cancellation-settings'),
  createCancellationSetting: (data) => api.post('/restaurant-cancellation-settings', data),
  updateCancellationSetting: (id, data) => api.put(`/restaurant-cancellation-settings/${id}`, data),
  deleteCancellationSetting: (id) => api.delete(`/restaurant-cancellation-settings/${id}`),

  // Admin / Partner routes
  getAllRestaurants: (params) => api.get('/admin/restaurants', { params }),
  getLocationOptions: (params) => api.get('/admin/restaurants/locations', { params }),
  getMyRestaurants: () => api.get('/partner/restaurants/my'),
  getRestaurantPartners: () => api.get('/partner/restaurants/partners'),
  createRestaurant: (data) => api.post('/partner/restaurants', data),
  updateRestaurant: (id, data) => api.put(`/partner/restaurants/${id}`, data),
  deleteRestaurant: (id) => api.delete(`/restaurants/${id}`),
  getAllBookings: (params) => api.get('/partner/restaurants/bookings/all', { params }),
  getInstantPayments: (params) => api.get('/partner/restaurants/instant-payments', { params }),

  // Invoices (commission split breakdown)
  getBookingInvoice: (id) => api.get(`/restaurants/booking/${id}/invoice`),
  getInstantPayInvoice: (id) => api.get(`/restaurants/instant-pay/${id}/invoice`),
  getWalkinInvoice: (id) => api.get(`/restaurants/walkin/${id}/invoice`),

  // Tables
  getTables: (restaurantId) => api.get(`/partner/restaurants/${restaurantId}/tables`),
  createTable: (restaurantId, data) => api.post(`/partner/restaurants/${restaurantId}/tables`, data),
  updateTable: (restaurantId, tableId, data) => api.put(`/partner/restaurants/${restaurantId}/tables/${tableId}`, data),
  deleteTable: (restaurantId, tableId) => api.delete(`/partner/restaurants/${restaurantId}/tables/${tableId}`),

  // Coupons
  getRestaurantCoupons: (restaurantId) => api.get(`/restaurant-coupons/restaurant/${restaurantId}`),

  // Markups
  getRestaurantMarkups: (restaurantId) => api.get(`/restaurant-markup/restaurant/${restaurantId}`),
  autoApplyRestaurantCoupon: (restaurantId, amount, bookingType) => api.post('/restaurant-coupons/auto-apply', { restaurantId, amount, bookingType }),
  validateRestaurantCoupon: (code, restaurantId, amount, bookingType) => api.post('/restaurant-coupons/validate', { code, restaurantId, amount, bookingType }),
  getPartnerCoupons: (restaurantId) => api.get('/partner/restaurant-coupons', { params: { restaurantId: restaurantId || undefined } }),
  togglePartnerCoupon: (id, enabled, restaurantId) => api.patch(`/partner/restaurant-coupons/${id}/toggle`, { enabled, restaurantId }),
  createPartnerCoupon: (data) => api.post('/partner/restaurant-coupons', data),
  updatePartnerCoupon: (id, data) => api.put(`/partner/restaurant-coupons/${id}`, data),
  deletePartnerCoupon: (id) => api.delete(`/partner/restaurant-coupons/${id}`),

  // Stats
  getStats: (params) => api.get('/partner/restaurants/stats/overview', { params }),
};

export const holidayAPI = {
  searchHolidays: (params) => api.get('/holidays/search', { params }),
  getFeaturedHolidays: () => api.get('/holidays/featured'),
  getHoliday: (id) => api.get(`/holidays/${id}`),
  createBooking: (data) => api.post('/holidays/book', data),
  confirmBooking: (id, paymentData) => api.put(`/holidays/booking/${id}/confirm`, paymentData),
  getBooking: (reference) => api.get(`/holidays/booking/${reference}`),
  cancelBooking: (id, reason) => api.put(`/holidays/booking/${id}/cancel`, { reason }),
  getMyBookings: () => api.get('/holidays/bookings/my'),

  getAllHolidays: (params) => api.get('/holidays', { params }),
  createHoliday: (data) => api.post('/holidays', data),
  updateHoliday: (id, data) => api.put(`/holidays/${id}`, data),
  deleteHoliday: (id) => api.delete(`/holidays/${id}`),
  getAllBookings: (params) => api.get('/holidays/bookings/all', { params }),
};

export const carAPI = {
  searchCars: (params) => api.get('/cars/search', { params }),
  getCar: (id) => api.get(`/cars/${id}`),
  createBooking: (data) => api.post('/cars/book', data),
  confirmBooking: (id, paymentData) => api.put(`/cars/booking/${id}/confirm`, paymentData),
  getBooking: (reference) => api.get(`/cars/booking/${reference}`),
  cancelBooking: (id, reason) => api.put(`/cars/booking/${id}/cancel`, { reason }),
  getMyBookings: () => api.get('/cars/bookings/my'),

  getAllCars: (params) => api.get('/cars', { params }),
  createCar: (data) => api.post('/cars', data),
  updateCar: (id, data) => api.put(`/cars/${id}`, data),
  deleteCar: (id) => api.delete(`/cars/${id}`),
  getAllBookings: (params) => api.get('/cars/bookings/all', { params }),
};

export const trainAPI = {
  searchTrains: (params) => api.get('/trains/search', { params }),
  getTrain: (id) => api.get(`/trains/${id}`),
  createBooking: (data) => api.post('/trains/book', data),
  confirmBooking: (id, paymentData) => api.put(`/trains/booking/${id}/confirm`, paymentData),
  getBooking: (reference) => api.get(`/trains/booking/${reference}`),
  cancelBooking: (id, reason) => api.put(`/trains/booking/${id}/cancel`, { reason }),
  getMyBookings: () => api.get('/trains/bookings/my'),

  getAllTrains: (params) => api.get('/trains', { params }),
  createTrain: (data) => api.post('/trains', data),
  updateTrain: (id, data) => api.put(`/trains/${id}`, data),
  deleteTrain: (id) => api.delete(`/trains/${id}`),
  getAllBookings: (params) => api.get('/trains/bookings/all', { params }),
};

export const busAPI = {
  searchBuses: (params) => api.get('/buses/search', { params }),
  autocomplete: (q) => api.get('/buses/autocomplete', { params: { q } }),
  getBus: (id) => api.get(`/buses/${id}`),
  getBusSeats: (id, params) => api.get(`/buses/${id}/seats`, { params }),
  createBooking: (data) => api.post('/buses/book', data),
  pricingPreview: (data) => api.post('/buses/pricing-preview', data),
  validateOffer: (data) => api.post('/buses/validate-offer', data),
  confirmBooking: (id, paymentData) => api.put(`/buses/booking/${id}/confirm`, paymentData),
  getBooking: (reference) => api.get(`/buses/booking/${reference}`),
  cancelBooking: (id, reason) => api.put(`/buses/booking/${id}/cancel`, { reason }),
  getMyBookings: () => api.get('/buses/bookings/my'),
  getReviews: (busId) => api.get(`/buses/${busId}/reviews`),
  submitReview: (busId, formData) => api.post(`/buses/${busId}/reviews`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),

  // Admin endpoints
  getAllBuses: (params) => api.get('/buses', { params }),
  createBus: (data) => api.post('/buses', data),
  updateBus: (id, data) => api.put(`/buses/${id}`, data),
  deleteBus: (id) => api.delete(`/buses/${id}`),
  getAllBookings: (params) => api.get('/buses/bookings/all', { params }),
  getBusDetail: (id) => api.get(`/buses/${id}`),
};

export const busAdminAPI = {
  getPartners: () => api.get('/admin/buses/partners'),
  createBus: (data) => api.post('/admin/buses', data),
  getDeletedBuses: (params) => api.get('/admin/buses/deleted', { params }),
  restoreBus: (id) => api.put(`/admin/buses/${id}/restore`),
  getOperators: (params) => api.get('/admin/buses/operators', { params }),
  createOperator: (data) => api.post('/admin/buses/operators', data),
  updateOperator: (id, data) => api.put(`/admin/buses/operators/${id}`, data),
  deleteOperator: (id) => api.delete(`/admin/buses/operators/${id}`),
  verifyOperator: (id, status) => api.put(`/admin/buses/operators/${id}/verify`, { status }),
  getCommissions: () => api.get('/admin/buses/commissions'),
  createCommission: (data) => api.post('/admin/buses/commissions', data),
  updateCommission: (id, data) => api.put(`/admin/buses/commissions/${id}`, data),
  deleteCommission: (id) => api.delete(`/admin/buses/commissions/${id}`),
  getMarkups: () => api.get('/admin/buses/markups'),
  createMarkup: (data) => api.post('/admin/buses/markups', data),
  updateMarkup: (id, data) => api.put(`/admin/buses/markups/${id}`, data),
  deleteMarkup: (id) => api.delete(`/admin/buses/markups/${id}`),
  getTaxes: () => api.get('/admin/buses/taxes'),
  createTax: (data) => api.post('/admin/buses/taxes', data),
  updateTax: (id, data) => api.put(`/admin/buses/taxes/${id}`, data),
  deleteTax: (id) => api.delete(`/admin/buses/taxes/${id}`),
  getAllBookings: (params) => api.get('/admin/buses/bookings', { params }),
  getBookingInvoice: (id) => api.get(`/admin/buses/bookings/${id}/invoice`),
  getInventory: (params) => api.get('/admin/buses/inventory', { params }),
  getBookingStats: (params) => api.get('/admin/buses/bookings/stats', { params }),
  getAnalytics: (params) => api.get('/admin/buses/analytics', { params }),
  cancelBooking: (id, reason) => api.put(`/admin/buses/bookings/${id}/cancel`, { reason }),
  exportBookings: (params) => api.get('/admin/buses/bookings/export', { params }),
  // Offers
  getOffers: () => api.get('/admin/buses/offers'),
  createOffer: (data) => api.post('/admin/buses/offers', data),
  updateOffer: (id, data) => api.put(`/admin/buses/offers/${id}`, data),
  deleteOffer: (id) => api.delete(`/admin/buses/offers/${id}`),
};

// AI search (public) + AI settings (admin)
export const aiAPI = {
  getStatus: () => publicApi.get('/ai/status'),
  search: (data) => publicApi.post('/ai/search', data),
};

export const aiAdminAPI = {
  getSettings: () => api.get('/admin/ai/settings'),
  saveSettings: (data) => api.put('/admin/ai/settings', data),
  testConnection: () => api.post('/admin/ai/test'),
};

// Third-party bus API provider management (admin enable/disable + test)
export const busApiProviderAPI = {
  getProviders: () => api.get('/admin/bus-api/providers'),
  createProvider: (data) => api.post('/admin/bus-api/providers', data),
  updateProvider: (id, data) => api.put(`/admin/bus-api/providers/${id}`, data),
  deleteProvider: (id) => api.delete(`/admin/bus-api/providers/${id}`),
  toggleProvider: (id, isActive) => api.put(`/admin/bus-api/providers/${id}/toggle`, isActive !== undefined ? { isActive } : {}),
  testProvider: (id) => api.post(`/admin/bus-api/providers/${id}/test`),
};

export const busPartnerAPI = {
  getOperator: () => api.get('/partner/bus/operator'),
  saveOperator: (data) => api.post('/partner/bus/operator', data),
  verifyOperator: (formData) => api.post('/partner/bus/operator/verify', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  getDashboard: () => api.get('/partner/bus/dashboard'),
  getBuses: (params) => api.get('/partner/bus/buses', { params }),
  getBus: (id) => api.get(`/partner/bus/buses/${id}`),
  createBus: (data) => api.post('/partner/bus/buses', data),
  updateBus: (id, data) => api.put(`/partner/bus/buses/${id}`, data),
  deleteBus: (id) => api.delete(`/partner/bus/buses/${id}`),
  getInventory: (id, params) => api.get(`/partner/bus/buses/${id}/inventory`, { params }),
  createInventory: (id, data) => api.post(`/partner/bus/buses/${id}/inventory`, data),
  updateInventory: (id, invId, data) => api.put(`/partner/bus/buses/${id}/inventory/${invId}`, data),
  deleteInventory: (id, invId) => api.delete(`/partner/bus/buses/${id}/inventory/${invId}`),
  setOfflineBookings: (id, invId, data) => api.post(`/partner/bus/buses/${id}/inventory/${invId}/offline`, data),
  bulkCreateInventory: (id, data) => api.post(`/partner/bus/buses/${id}/inventory/bulk`, data),
  getBookings: (params) => api.get('/partner/bus/bookings', { params }),
  getBookingStats: () => api.get('/partner/bus/bookings/stats'),
  getBookingInvoice: (id) => api.get(`/partner/bus/bookings/${id}/invoice`),
  cancelBooking: (id, reason) => api.put(`/partner/bus/bookings/${id}/cancel`, { reason }),
  exportBookings: (params) => api.get('/partner/bus/bookings/export', { params }),
  getAnalytics: (params) => api.get('/partner/bus/analytics', { params }),
  getSettlement: () => api.get('/partner/bus/payout/settlement'),
  getProfile: () => api.get('/partner/bus/profile'),
  updateProfile: (data) => api.put('/partner/bus/profile', data),
  uploadBusDocuments: (id, formData) => api.post(`/partner/bus/buses/${id}/documents`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deleteBusDocument: (id, docId) => api.delete(`/partner/bus/buses/${id}/documents/${docId}`),
  uploadBusImage: (id, formData) => api.post(`/partner/bus/buses/${id}/images`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deleteBusImage: (id, index) => api.delete(`/partner/bus/buses/${id}/images/${index}`),
  submitBus: (id) => api.post(`/partner/bus/buses/${id}/submit`),
  getLocations: (params) => api.get('/partner/bus/locations', { params }),
};

// Bus partner personal KYC (Aadhaar/PAN + address) — gates the partner panel
export const busKycAPI = {
  getMyKyc: () => api.get('/partner/bus-kyc'),
  saveKyc: (formData) => api.post('/partner/bus-kyc', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
};

// Admin two-stage verification (partner KYC + per-bus documents)
export const busVerificationAdminAPI = {
  // Combined queue: one item = partner KYC + bus, reviewed together
  getSubmissions: (status) => api.get('/admin/bus-verification/submissions', { params: status ? { status } : {} }),
  review: (body) => api.put('/admin/bus-verification/review', body),
  getKycList: (status) => api.get('/admin/bus-verification/kyc', { params: status ? { status } : {} }),
  reviewKyc: (id, body) => api.put(`/admin/bus-verification/kyc/${id}/review`, body),
  getBusList: (status) => api.get('/admin/bus-verification/buses', { params: status ? { status } : {} }),
  reviewBus: (id, body) => api.put(`/admin/bus-verification/buses/${id}/review`, body),
};

export const paymentGatewayAPI = {
  getAll: () => api.get('/admin/payment-gateways'),
  update: (gateway, data) => api.put(`/admin/payment-gateways/${gateway}`, data),
  toggle: (gateway) => api.put(`/admin/payment-gateways/${gateway}/toggle`),
  getActive: () => api.get('/payment-gateways/public'),
};

// Dedicated Razorpay Payout (RazorpayX) credentials — separate from customer payment collection
export const payoutConfigAPI = {
  getConfig: () => api.get('/admin/razorpay-payout-config'),
  saveConfig: (data) => api.put('/admin/razorpay-payout-config', data),
};

export const payoutAPI = {
  getBalance: () => api.get('/payout/partner/balance'),
  getCompletedBookings: (params) => api.get('/payout/partner/completed-bookings', { params }),
  createRequest: (data) => api.post('/payout/partner/request', data),
  getPartnerRequests: () => api.get('/payout/partner/requests'),
  getPayoutMethod: () => api.get('/payout/partner/payout-method'),
  savePayoutMethod: (data) => api.put('/payout/partner/payout-method', data),
  getAdminRequests: (params) => api.get('/payout/admin/requests', { params }),
  approveRequest: (id) => api.put(`/payout/admin/${id}/approve`),
  payRequest: (id, data) => api.put(`/payout/admin/${id}/pay`, data),
  rejectRequest: (id, data) => api.put(`/payout/admin/${id}/reject`, data),
};

// Restaurant partner payout (settlement + withdrawal)
export const restaurantPayoutAPI = {
  getSettlements: () => api.get('/partner/restaurant-payout/settlements'),
  getSettlementHistory: () => api.get('/partner/restaurant-payout/settlements/history'),
  settle: (kind, id) => api.post(`/partner/restaurant-payout/settlements/${kind}/${id}/settle`),
  getWallets: () => api.get('/partner/restaurant-payout/wallets'),
  getWithdrawals: () => api.get('/partner/restaurant-payout/withdrawals'),
  createWithdrawal: (data) => api.post('/partner/restaurant-payout/withdrawals', data),
  getPayoutMethods: () => api.get('/partner/restaurant-payout/payout-methods'),
  getPayoutMethod: (restaurantId) => api.get(`/partner/restaurant-payout/payout-methods/${restaurantId}`),
  savePayoutMethod: (restaurantId, data) => api.put(`/partner/restaurant-payout/payout-methods/${restaurantId}`, data),
  deletePayoutMethod: (restaurantId) => api.delete(`/partner/restaurant-payout/payout-methods/${restaurantId}`),
  // Admin
  getAdminRequests: (params) => api.get('/partner/restaurant-payout/admin/requests', { params }),
  approveAdminRequest: (id) => api.put(`/partner/restaurant-payout/admin/${id}/approve`),
  payAdminRequest: (id) => api.put(`/partner/restaurant-payout/admin/${id}/pay`),
  rejectAdminRequest: (id, data) => api.put(`/partner/restaurant-payout/admin/${id}/reject`, data),
  checkAdminRequestStatus: (id) => api.get(`/partner/restaurant-payout/admin/${id}/status`),
};

// Hotel partner payout (settlement + withdrawal)
export const hotelPayoutAPI = {
  getSettlements: (hotelId) => api.get('/partner/hotel-payout/settlements', { params: { hotelId } }),
  getSettlementHistory: (hotelId) => api.get('/partner/hotel-payout/settlements/history', { params: { hotelId } }),
  settle: (id) => api.post(`/partner/hotel-payout/settlements/${id}/settle`),
  getSummary: (hotelId) => api.get('/partner/hotel-payout/summary', { params: { hotelId } }),
  getWithdrawals: (hotelId) => api.get('/partner/hotel-payout/withdrawals', { params: { hotelId } }),
  createWithdrawal: (data) => api.post('/partner/hotel-payout/withdrawals', data),
  getPayoutMethods: () => api.get('/partner/hotel-payout/payout-methods'),
  getPayoutMethod: (hotelId) => api.get(`/partner/hotel-payout/payout-methods/${hotelId}`),
  savePayoutMethod: (hotelId, data) => api.put(`/partner/hotel-payout/payout-methods/${hotelId}`, data),
  deletePayoutMethod: (hotelId) => api.delete(`/partner/hotel-payout/payout-methods/${hotelId}`),
  getTaxProfiles: () => api.get('/partner/hotel-payout/tax-profiles'),
  getTaxProfile: (hotelId) => api.get(`/partner/hotel-payout/tax-profiles/${hotelId}`),
  saveTaxProfile: (hotelId, data) => api.put(`/partner/hotel-payout/tax-profiles/${hotelId}`, data),
};

// Admin Restaurant Section APIs
export const partnerVerificationAPI = {
  getAll: (params) => api.get('/admin/partner-verification', { params }),
  getById: (id) => api.get(`/admin/partner-verification/${id}`),
  review: (id, data) => api.put(`/admin/partner-verification/${id}/review`, data),
  suspend: (id, data) => api.put(`/admin/partner-verification/${id}/suspend`, data),
  restore: (id) => api.put(`/admin/partner-verification/${id}/restore`),
  reverify: (id, data) => api.put(`/admin/partner-verification/${id}/reverify`, data),
  verifyDocument: (id, data) => api.put(`/admin/partner-verification/${id}/documents/verify`, data),
};

// Partner-side restaurant verification
export const restaurantVerificationAPI = {
  getMy: () => api.get('/partner/verification'),
  uploadRestaurantDoc: (fd) => api.post('/partner/verification/documents/restaurant', fd),
  uploadPersonDoc: (fd) => api.post('/partner/verification/documents/person', fd),
  uploadPhoto: (fd) => api.post('/partner/verification/documents/photo', fd),
  submit: (data) => api.post('/partner/verification/submit', data),
};

// Partner-side hotel verification
export const hotelVerificationAPI = {
  getMy: () => api.get('/partner/hotel-verification'),
  uploadHotelDoc: (fd) => api.post('/partner/hotel-verification/documents/hotel', fd),
  uploadPersonDoc: (fd) => api.post('/partner/hotel-verification/documents/person', fd),
  uploadPhoto: (fd) => api.post('/partner/hotel-verification/documents/photo', fd),
  submit: (data) => api.post('/partner/hotel-verification/submit', data),
};

// Admin: hotel partner verification
export const adminHotelVerificationAPI = {
  getAll: (params) => api.get('/admin/hotel-partner-verification', { params }),
  verifyDocument: (id, key, verified) => api.post(`/admin/hotel-partner-verification/${id}/documents/${key}`, { verified }),
  review: (id, data) => api.post(`/admin/hotel-partner-verification/${id}/review`, data),
  reverify: (id, data) => api.post(`/admin/hotel-partner-verification/${id}/reverify`, data),
  suspend: (id, data) => api.post(`/admin/hotel-partner-verification/${id}/suspend`, data),
  restore: (id) => api.post(`/admin/hotel-partner-verification/${id}/restore`),
};

export const instantPayAPI = {
  getAll: (params) => api.get('/admin/instant-pay', { params }),
  getFlagged: () => api.get('/admin/instant-pay/flagged'),
  flag: (id, data) => api.put(`/admin/instant-pay/${id}/flag`, data),
  unflag: (id) => api.put(`/admin/instant-pay/${id}/unflag`),
  settle: (id, data) => api.put(`/admin/instant-pay/${id}/settle`, data),
  pendingSettlements: () => api.get('/admin/instant-pay/settlements/pending'),
};

export const bookingDisputeAPI = {
  getAll: (params) => api.get('/admin/booking-disputes', { params }),
  resolve: (id, data) => api.put(`/admin/booking-disputes/${id}/resolve`, data),
  dismiss: (id) => api.put(`/admin/booking-disputes/${id}/dismiss`),
};

export const contentCMSAPI = {
  // Banners
  getBanners: () => api.get('/admin/restaurant-content-cms/banners'),
  createBanner: (data) => api.post('/admin/restaurant-content-cms/banners', data),
  updateBanner: (id, data) => api.put(`/admin/restaurant-content-cms/banners/${id}`, data),
  deleteBanner: (id) => api.delete(`/admin/restaurant-content-cms/banners/${id}`),
  // Cuisines
  getCuisines: () => api.get('/admin/restaurant-content-cms/cuisines'),
  createCuisine: (data) => api.post('/admin/restaurant-content-cms/cuisines', data),
  updateCuisine: (id, data) => api.put(`/admin/restaurant-content-cms/cuisines/${id}`, data),
  deleteCuisine: (id) => api.delete(`/admin/restaurant-content-cms/cuisines/${id}`),
  // City Pages
  getCities: () => api.get('/admin/restaurant-content-cms/cities'),
  createCity: (data) => api.post('/admin/restaurant-content-cms/cities', data),
  updateCity: (id, data) => api.put(`/admin/restaurant-content-cms/cities/${id}`, data),
  deleteCity: (id) => api.delete(`/admin/restaurant-content-cms/cities/${id}`),
  // Editorials
  getEditorials: () => api.get('/admin/restaurant-content-cms/editorials'),
  createEditorial: (data) => api.post('/admin/restaurant-content-cms/editorials', data),
  updateEditorial: (id, data) => api.put(`/admin/restaurant-content-cms/editorials/${id}`, data),
  deleteEditorial: (id) => api.delete(`/admin/restaurant-content-cms/editorials/${id}`),
};

export const restaurantAnalyticsAPI = {
  getPlatform: () => api.get('/admin/restaurant-analytics/platform'),
  getCities: () => api.get('/admin/restaurant-analytics/cities'),
  getTopRestaurants: (params) => api.get('/admin/restaurant-analytics/top-restaurants', { params }),
  getGrowth: (params) => api.get('/admin/restaurant-analytics/growth', { params }),
  getCuisinePerformance: () => api.get('/admin/restaurant-analytics/cuisine-performance'),
  getRevenueReport: (params) => api.get('/admin/restaurant-analytics/revenue-report', { params }),
};

export const hotelAnalyticsAPI = {
  getRevenueReport: (params) => api.get('/admin/hotel-analytics/revenue-report', { params }),
  getBookingInvoice: (id) => api.get(`/admin/hotel-analytics/bookings/${id}/invoice`, { responseType: 'text' }),
};

export const hotelAnalyticsPartnerAPI = {
  getRevenueReport: (params) => api.get('/partner/hotel-analytics/revenue-report', { params }),
};

// Health check
export const healthCheck = () => api.get('/health');

export default api;
export { api };

